import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AppShell } from "./components/app-shell";
import { useAuth, useBootstrapAuth } from "./lib/auth";
import { HomePage } from "./pages/home";
import { LoginPage } from "./pages/login";
import { OAuthCallbackPage } from "./pages/oauth-callback";
import { LogoutPage } from "./pages/logout";
import { NotFoundPage } from "./pages/not-found";
import { CatalogPage } from "./pages/catalog";
import { OutcomeDetailPage } from "./pages/outcome-detail";
import { JobsListPage } from "./pages/jobs-list";
import { JobDetailPage } from "./pages/job-detail";
import { WalletPage } from "./pages/wallet";
import { ConnectedAppsPage } from "./pages/connected-apps";
import { FavoritesPage } from "./pages/favorites";

export function App() {
  const bootstrap = useBootstrapAuth();
  const loaded = useAuth((s) => s.loaded);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  if (!loaded) {
    return <LoadingSplash />;
  }

  return (
    <Routes>
      {/* Auth flow pages render without the app shell. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

      {/* Everything else shares the shell (header, nav, theme toggle, mobile drawer). */}
      <Route element={<ShelledLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route
          path="/outcomes/:partner_slug/:outcome_slug"
          element={<OutcomeDetailPage />}
        />
        <Route path="/jobs" element={<RequireAuth><JobsListPage /></RequireAuth>} />
        <Route path="/jobs/:id" element={<RequireAuth><JobDetailPage /></RequireAuth>} />
        <Route path="/wallet" element={<RequireAuth><WalletPage /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><ComingSoon title="Settings" /></RequireAuth>} />
        <Route path="/settings/connected-apps" element={<RequireAuth><ConnectedAppsPage /></RequireAuth>} />
        <Route path="/favorites" element={<RequireAuth><FavoritesPage /></RequireAuth>} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function ShelledLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function LoadingSplash() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const location = useLocation();
  if (!user) {
    // Preserve return path.
    sessionStorage.setItem("oauth_return_to", location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function ComingSoon({ title }: { title: string }) {
  return (
    <div className="mx-auto mt-24 max-w-xl px-4 text-center">
      <h1 className="text-3xl font-semibold">{title}</h1>
      <p className="mt-4 text-neutral-500">
        This page is scaffolded. Wiring lands in the next milestone.
      </p>
    </div>
  );
}
